// src/pages/income/service/YongyeokGisungModal.jsx
// ✅ 용역사업 "기성계 불러오기" 모달 — (구) 페이지(draft_document/TemplateFormFields.jsx)
// 에 있던 모달을 통합 작성 페이지용으로 독립 컴포넌트화. 연도/월 셀렉트 + 미작성/작성/
// 전체 토글(기본 미작성, gisung_gye 필터 인프라 재사용) — 선택한 레코드를 "기준"(N회차)
// 삼아 N+1회차 필드를 계산해 onPick으로 돌려준다((구) 페이지의 commitYongyeokFields와
// 동일한 관례 — 정정/재기안 시 과거 회차를 기준으로 다시 잡는 용도, 청구형에 한함).
//
// ✅ 전역 조회(20개 계약 전체) — 2026-07 기성계 관리 화면 실측 결과 제출형 13곳도
// 전부 실제로 기성계가 입력되고 있어(연 116건, 구청 78/공단 38), 청구형 7곳으로
// 좁혀뒀던 걸 20곳 전체로 확장. contractId prop은 받지 않는다 — VENDOR_CONFIG의
// 20개 항목(청구형 7 + 제출형 13, 전부 contractId 보유) 전체를 조회해 하나의
// 목록으로 합치고, 고른 레코드가 어느 거래처·어느 유형(청구형/제출형)인지 onPick으로
// 함께 돌려줘서 호출부(IncomeServiceDraftNew.jsx)가 거래처 선택 + 유형별 로직
// (청구형=페어/제출형=단건)을 맞춰준다.
import React, { useEffect, useState } from "react";
import useBackdropClose from "../../../hooks/useBackdropClose";
import api from "../../../api";
import { VENDOR_CONFIG } from "../../../components/document-type/ServiceGiseongAutoFill";

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 3 + i);

// ✅ VENDOR_CONFIG에서 contractId가 있는 전 항목(청구형+제출형, 20곳) 목록화.
// 계약 ID의 출처를 이 파일 하나로 유지(중복 하드코딩 방지) — VENDOR_CONFIG가 그대로
// 단일 진실 공급원.
const ALL_GISUNG_VENDORS = Object.entries(VENDOR_CONFIG)
  .filter(([, cfg]) => cfg.contractId)
  .map(([vendorName, cfg]) => ({ vendorName, contractId: cfg.contractId, tableType: cfg.tableType }));

// ✅ 전회차 레코드 하나로부터 "다음 회차(N+1)" 필드값 계산 — (구) 페이지와 동일 로직.
// 청구형(내부기안+공문 페어, 회차 청구)에서만 의미가 있다 — 제출형은 매달 단건
// 제출이라 이 계산 자체가 필요 없다(호출하지 않음).
// prev_amount 필드는 신뢰하지 않고(round 3~4 이후 어긋나는 데이터 오류 다수 확인됨)
// 레코드 자신의 current_amount/remaining_amount를 쓴다.
function fieldsFromPrevRecord(prevRecord) {
  const round = (prevRecord?.gisung_round ?? 0) + 1;
  const contractAmount = Number(prevRecord?.contract_amount) || 0;
  const prevAmount = prevRecord ? Number(prevRecord.current_amount) || 0 : 0;
  const prevCumulative = contractAmount - (prevRecord ? Number(prevRecord.remaining_amount) || 0 : contractAmount);
  return { round, contractAmount, prevAmount, prevCumulative };
}

export default function YongyeokGisungModal({ onClose, onPick }) {
  const backdrop = useBackdropClose(onClose);

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("");
  const [draftedFilter, setDraftedFilter] = useState("undrafted");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (year) params.year = year;
        if (month) params.month = month;
        if (draftedFilter !== "all") params.is_drafted = draftedFilter === "drafted";
        // ✅ 계약 20곳을 병렬 조회 후 합침(백엔드는 계약 단위 필터만 지원 — department
        // 등으로 한 번에 좁히는 필터가 없어 프론트에서 VENDOR_CONFIG 기준으로 좁힘).
        const results = await Promise.all(
          ALL_GISUNG_VENDORS.map(({ vendorName, contractId, tableType }) =>
            api.get("gisung-gye/records/", { params: { ...params, contract: contractId } })
              .then(({ data }) => (Array.isArray(data) ? data : data.results || []).map((r) => ({ ...r, _vendorName: vendorName, _tableType: tableType })))
              .catch((err) => {
                console.error(`❌ 기성계 조회 실패(${vendorName}):`, err);
                return [];
              })
          )
        );
        const merged = results.flat().sort((a, b) => {
          if (a.gisung_year !== b.gisung_year) return b.gisung_year - a.gisung_year;
          if (a.gisung_month !== b.gisung_month) return b.gisung_month - a.gisung_month;
          if (a._vendorName !== b._vendorName) return a._vendorName.localeCompare(b._vendorName, "ko");
          return b.gisung_round - a.gisung_round;
        });
        if (!ignore) setList(merged);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [year, month, draftedFilter]);

  // ✅ 작성 여부 판정 — record.is_drafted/drafted_document는 백엔드
  // GisungRecordSerializer가 DraftDocument 역참조(related_name="draft_documents")로
  // 계산해 내려주는 값(gisung_gye/serializers.py)이고, 위 useEffect의 미작성/작성
  // 필터(is_drafted 쿼리 파라미터)도 정확히 같은 필드를 기준으로 서버에서 걸러진다
  // — "전체" 탭에서도 행별로 이 값이 그대로 내려오므로 별도 판정 로직을 새로 만들지
  // 않고 이미 있는 값을 그대로 뱃지·재작성 확인에 재사용한다(판정 기준 불일치 없음).
  const handlePick = (record) => {
    if (record.is_drafted) {
      const no = record.drafted_document?.draft_no || "";
      const ok = window.confirm(`이미 ${no}로 기안이 작성된 기성계입니다. 새로 작성하시겠습니까?`);
      if (!ok) return;
    }
    if (record._tableType === "yongyeok_claim") {
      // ✅ source — (구) IncomeServiceNew.jsx의 "📌 내가 선택한 자료" 카드 재현용.
      // 계산된 N+1회차 필드와 별개로, 지금 고른 원본 레코드 자체의 정보를 그대로 담아
      // 돌려준다(IncomeServiceDraftNew.jsx가 gisungSource로 보관해 카드에 표시).
      onPick({
        ...fieldsFromPrevRecord(record),
        recordId: record.id,
        vendorName: record._vendorName,
        tableType: "yongyeok_claim",
        source: {
          gisung_year: record.gisung_year, gisung_month: record.gisung_month, gisung_round: record.gisung_round,
          institution: record.institution, service_name: record.service_name, current_amount: record.current_amount,
        },
      });
    } else {
      // ✅ 제출형 — 회차/계약금액 계산이 필요 없다(매달 단건 제출). 거래처 전환과
      // recordId(제출 저장 시 gisung_record로 연결해 "작성됨" 필터가 정확히 반영되게)만
      // 전달한다.
      onPick({ recordId: record.id, vendorName: record._vendorName, tableType: "yongyeok_submit" });
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} {...backdrop}>
      <div style={{ background: "#fff", borderRadius: 8, padding: 20, width: 760, maxHeight: "70vh", overflowY: "auto" }}>
        <h3 style={{ marginTop: 0, whiteSpace: "nowrap" }}>기성계 이력 불러오기 (전체)</h3>

        {/* ✅ 연도/월 select 폭 명시(120px/100px) — 이 파일에 폭을 안 주면 전역
            CSS 누수(아래 참고)에 그대로 걸려 100%로 늘어나 두 줄로 떨어진다.
            CLAUDE.md 규칙대로 개별 width 지정으로 막는다. flexWrap도 nowrap으로
            고정해 좌측(연/월)·우측(필터 버튼)이 항상 한 줄에 오도록 함.
            ⚠️ 근본 원인: IncomeServiceNew.module.css:178 등 여러 .module.css가
            "input, textarea, select { width: 100%; ... }"처럼 클래스 없이 요소
            셀렉터만 써서 규칙을 적었다 — CSS Modules는 클래스/id만 스코프하고
            요소 셀렉터는 스코프하지 않으므로 이 규칙이 앱 전역에 그대로 새어나간다
            (CLAUDE.md에 이미 기록된 button{width:100%} 누수와 동일한 원인 패턴).
            (구) IncomeServiceNew.jsx가 여전히 라우팅돼 번들에 포함돼 있어 이
            모달까지 영향을 받음 — 근본 수정(10개 이상 파일의 전역 규칙 정리)은
            이번 범위 밖이라 이 컴포넌트에서 명시적 width로 방어만 함. */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap", marginBottom: 12 }}>
          <select value={year} onChange={(e) => setYear(e.target.value)} style={{ width: 120, flexShrink: 0, padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid #ccc" }}>
            {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 100, flexShrink: 0, padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid #ccc" }}>
            <option value="">전체 월</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}월</option>)}
          </select>
          <div style={{ display: "flex", gap: 4, marginLeft: "auto", flexShrink: 0 }}>
            {[{ value: "undrafted", label: "미작성" }, { value: "drafted", label: "작성" }, { value: "all", label: "전체" }].map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setDraftedFilter(t.value)}
                style={{
                  padding: "6px 12px", fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                  border: draftedFilter === t.value ? "1px solid #1565C0" : "1px solid #ccc",
                  background: draftedFilter === t.value ? "#1565C0" : "#fff",
                  color: draftedFilter === t.value ? "#fff" : "#37474f",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>불러오는 중...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>기성계 이력이 없습니다.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>구분</th>
                <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>거래처</th>
                <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>회차</th>
                <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>귀속연월</th>
                <th style={{ textAlign: "right", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>금회기성금액</th>
                <th style={{ padding: "6px 4px", borderBottom: "1px solid #ddd" }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} style={{ background: r.is_drafted ? "#f3f4f6" : "transparent" }}>
                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>
                    <span style={{
                      display: "inline-block", padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                      background: r._tableType === "yongyeok_claim" ? "#dbeafe" : "#fef3c7",
                      color: r._tableType === "yongyeok_claim" ? "#1e40af" : "#92400e",
                    }}>
                      {r._tableType === "yongyeok_claim" ? "청구형" : "제출형"}
                    </span>
                  </td>
                  <td style={{ padding: "6px 4px", maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r._vendorName}>{r._vendorName}</td>
                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{r.gisung_round}회</td>
                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{r.gisung_year}년 {r.gisung_month}월</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", whiteSpace: "nowrap" }}>{Number(r.current_amount || 0).toLocaleString()}원</td>
                  <td style={{ padding: "6px 4px" }}>
                    {/* ✅ "전체" 탭에서도 작성 여부를 구분할 수 있어야 하므로, 작성된
                        건도 버튼을 숨기지 않고 배지+버튼을 같이 보여준다. 선택하면
                        handlePick이 재작성 confirm을 먼저 띄운다(수정·재작성 실무
                        케이스를 막지 않기 위함 — 비활성화 대신 확인창). */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                      {r.is_drafted && (
                        <span
                          title={r.drafted_document?.draft_no ? `기안번호: ${r.drafted_document.draft_no}` : "작성완료"}
                          style={{ display: "inline-block", whiteSpace: "nowrap", padding: "3px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "#e5e7eb", color: "#4b5563" }}
                        >
                          작성완료{r.drafted_document?.draft_no ? ` · ${r.drafted_document.draft_no}` : ""}
                        </span>
                      )}
                      <button type="button" onClick={() => handlePick(r)} style={{ width: 56, height: 28, flexShrink: 0 }}>선택</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
